import { useEffect, useState } from "react";

import { PlanetMark } from "@/components/planet-mark";
import { cn } from "@/lib/utils";

const LINES = [
  "Know exactly who to reach out to today.",
  "The Signal Engine watches them around the clock.",
  "Private and encrypted. Never shared, never sold.",
];

const LINE_MS = 1100;
const FADE_MS = 300;
const WEB_HANDOFF_MS = 900;

/**
 * First-paint boot screen. Covers the native WebView cold load with the
 * animated Pyng mark and a phone mock cycling the hero copy, then hands off
 * to the app (and hides the native splash) once the first route is live.
 */
export function BootSplash() {
  const [line, setLine] = useState(0);
  const [leaving, setLeaving] = useState(false);
  const [gone, setGone] = useState(false);
  // Native shells only. On the web the real page should paint immediately —
  // no loader over the marketing site or deep links.
  const [native, setNative] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    const cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
    setNative(!!cap?.isNativePlatform?.());
    setChecked(true);
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => setLine((i) => (i + 1) % LINES.length), LINE_MS);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let paintFrame = 0;
    let handoffFrame = 0;
    const finish = () => {
      if (cancelled) return;
      setLeaving(true);
      window.setTimeout(() => !cancelled && setGone(true), FADE_MS);
    };

    // Effects run after React commits, but WKWebView may not have composited
    // that commit yet. Wait for two paint frames before removing the native
    // layer so it hands directly to this screen instead of a black frame.
    paintFrame = window.requestAnimationFrame(() => {
      handoffFrame = window.requestAnimationFrame(() => {
        void (async () => {
          try {
            const { Capacitor } = await import("@capacitor/core");
            if (!Capacitor.isNativePlatform() || cancelled) return;
            const { SplashScreen } = await import("@capacitor/splash-screen");
            void SplashScreen.hide().catch(() => undefined);
          } catch {
            /* Browser or unavailable native bridge — timed auto-hide remains. */
          }
        })();
      });
    });

    // This layer only bridges the native artwork to the ready route. Do not
    // impose a multi-second marketing animation after the app is interactive.
    const minimum = window.setTimeout(finish, WEB_HANDOFF_MS);
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(paintFrame);
      window.cancelAnimationFrame(handoffFrame);
      window.clearTimeout(minimum);
    };
  }, []);

  if (gone) return null;
  if (!native) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Loading Pyng"
      className={cn(
        "fixed inset-0 z-[100] flex flex-col items-center justify-center gap-8 bg-background px-6 transition-opacity",
        leaving ? "opacity-0" : "opacity-100",
      )}
      style={{ transitionDuration: `${FADE_MS}ms` }}
    >
      <PlanetMark size={72} animated />

      {/* Phone mock cycling the hero copy */}
      <div className="w-[210px] rounded-[2rem] border border-border/60 bg-card/60 p-2 shadow-soft">
        <div className="rounded-[1.6rem] bg-background/80 px-4 py-6 ring-1 ring-primary/10">
          <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-foreground/15" />
          <div className="relative h-[76px]">
            {LINES.map((text, i) => (
              <p
                key={text}
                className={cn(
                  "absolute inset-0 text-center text-[13px] leading-relaxed text-foreground/90 transition-opacity duration-500",
                  i === line ? "opacity-100" : "opacity-0",
                )}
              >
                {text}
              </p>
            ))}
          </div>
        </div>
      </div>

      <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Pyng</p>
    </div>
  );
}

export default BootSplash;